import React, { useState, useEffect } from 'react';
import { TextField, Paper, Tabs, Tab, Divider, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';
import { addDayToToday, getTodaySqlFormat } from '../helpers/functions';
import { getListeClient } from '../actions';
import { clientOnlyChild } from '../selectors/clients';
import logoSrc from '../images/logo.svg';
import signatureSrc from '../images/signaturetp.jpg';

// ─── jsPDF helpers ──────────────────────────────────────────────────────────

const drawBorder = (doc, x, y, w, h, borders) => {
    if (borders.includes('L') || borders.includes('l')) doc.line(x, y, x, y + h);
    if (borders.includes('T') || borders.includes('t')) doc.line(x, y, x + w, y);
    if (borders.includes('R') || borders.includes('r')) doc.line(x + w, y, x + w, y + h);
    if (borders.includes('B') || borders.includes('b')) doc.line(x, y + h, x + w, y + h);
};

/** Y baseline for text vertically centred in a cell */
const tY = (doc, cellY, cellH) => cellY + cellH / 2 + doc.getFontSize() * 0.3528 * 0.4;

const cellText = (doc, str, x, y, w, h, align = 'C') => {
    const baseline = tY(doc, y, h);
    if (align === 'C') doc.text(str, x + w / 2, baseline, { align: 'center' });
    else if (align === 'L') doc.text(str, x + 1, baseline);
    else if (align === 'R') doc.text(str, x + w - 1, baseline, { align: 'right' });
};

/** Dessine un label style DOMICILIATION : lignes partielles autour du texte. */
const drawLabeledLine = (doc, label, lineY, boxLeft, boxRight) => {
    const prevSize = doc.getFontSize();
    doc.setFontSize(7);
    const tw = doc.getTextWidth(label);
    const cx = (boxLeft + boxRight) / 2;
    const pad = 1;
    const tl = cx - tw / 2 - pad;
    const tr = cx + tw / 2 + pad;
    if (tl > boxLeft) doc.line(boxLeft, lineY, tl, lineY);
    if (tr < boxRight) doc.line(tr, lineY, boxRight, lineY);
    cellText(doc, label, boxLeft, lineY - 2.5, boxRight - boxLeft, 5, 'C');
    doc.setFontSize(prevSize);
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
    page: { padding: '32px', background: '#f8f9fa', minHeight: '100vh' },
    pageTitle: { margin: '0 0 20px', fontSize: 22, fontWeight: 400, color: '#202124' },
    tabsWrap: {
        background: '#fff', borderRadius: 8, marginBottom: 24,
        boxShadow: '0 1px 2px rgba(60,64,67,.3), 0 2px 6px 2px rgba(60,64,67,.15)'
    },
    card: {
        background: '#fff', borderRadius: 8, padding: '20px 24px', marginBottom: 16,
        boxShadow: '0 1px 2px rgba(60,64,67,.3), 0 2px 6px 2px rgba(60,64,67,.15)'
    },
    cardTitle: {
        margin: '0 0 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: '#5f6368'
    },
    layout: { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' },
    leftCol: { flex: '1 1 360px', minWidth: 0 },
    rightCol: { flex: '0 0 272px' },
    row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    col: { flex: '1 1 140px', minWidth: 0 },
    divider: { margin: '20px 0' },
    btn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', height: 40, background: '#1a73e8', color: '#fff',
        border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 500,
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)'
    },
};

// ────────────────────────────────────────────────────────────────────────────

const Traite = () => {
    const dispatch = useDispatch();
    const clientList = useSelector(clientOnlyChild);

    useEffect(() => { dispatch(getListeClient()); }, [dispatch]);

    const [mode, setMode] = useState(0); // 0 = client, 1 = fournisseur
    const [loading, setLoading] = useState(false);

    // Shared
    const [crea, setCrea] = useState(getTodaySqlFormat());
    const [eche, setEche] = useState(addDayToToday(30));
    const [valeur, setValeur] = useState('');
    const [nbDecoupe, setNbDecoupe] = useState(1);
    const [numEffet, setNumEffet] = useState(1500);

    // Client — tiré
    const [clNom, setClNom] = useState('');
    const [clAdresse, setClAdresse] = useState('');
    const [clCp, setClCp] = useState('');
    const [clCodeAgence, setClCodeAgence] = useState('');
    const [clCodeGuichet, setClCodeGuichet] = useState('');
    const [clCompte, setClCompte] = useState('');
    const [clCleRib, setClCleRib] = useState('');
    const [clVille, setClVille] = useState('');
    const [atcRib, setAtcRib] = useState('30002 02800 0000901959Y 74');

    // Fournisseur — banque tiré (ATC)
    const [codeAgence, setCodeAgence] = useState('');
    const [codeGuichet, setCodeGuichet] = useState('');
    const [compte, setCompte] = useState('');
    const [cleRib, setCleRib] = useState('');
    const [globalVille, setGlobalVille] = useState('');

    const [leCompteSupplier, setLeCompteSupplier] = useState('30004 03120 00011413276 16');
    const [raisonSocialeSupplier, setRaisonSocialeSupplier] = useState('SHARON SOUSSANA');

    const banks = [
        { value: '1', label: 'LCL' },
        { value: '2', label: 'CREDIT MUTUEL' },
        { value: '3', label: 'BANQUE POPULAIRE' },
        { value: '4', label: 'PERSONNALISE' },
    ];

    const suppliers = [
        { value: '1', label: 'SHARON SOUSSANA' },
        { value: '4', label: 'PERSONNALISE' },
    ];

    const handleBankChange = (val) => {
        const p = {
            '1': { a: '30002', g: '02800', c: '0000901959Y', k: '74', v: 'LCL MARSEILLE' },
            '2': { a: '10278', g: '00866', c: '00020307601', k: '60', v: 'CRCMM MARSEILLE ENTREPRISES' },
            '3': { a: '14607', g: '00075', c: '70721002745', k: '51', v: 'BANQUE POPULAIRE MEDITERRANEE' },
        }[val] || { a: '', g: '', c: '', k: '', v: '' };
        setCodeAgence(p.a); setCodeGuichet(p.g);
        setCompte(p.c); setCleRib(p.k); setGlobalVille(p.v);
    };

    const handleBankSupplier = (val) => {
        if (val === '1') { setLeCompteSupplier('30004 03120 00011413276 16'); setRaisonSocialeSupplier('SHARON SOUSSANA'); }
        else { setLeCompteSupplier(''); setRaisonSocialeSupplier(''); }
    };

    const handleAtcBankChange = (val) => {
        const ribs = {
            '1': '30002 02800 0000901959Y 74',
            '2': '10278 00866 00020307601 60',
            '3': '14607 00075 70721002745 51',
        };
        setAtcRib(ribs[val] || '');
    };

    // ─── Sélection du client (affectation 100% locale, pas de requête réseau parasite) ───
    const handleClientSelect = (cl) => {
        if (!cl) {
            setClNom(''); setClAdresse(''); setClCp('');
            setClCodeAgence(''); setClCodeGuichet('');
            setClCompte(''); setClCleRib(''); setClVille('');
            return;
        }

        setClNom(cl.label || cl.Nom_agence || '');
        setClAdresse(cl.Rue || '');
        setClCp(`${cl.Code_postal || ''} ${cl.Ville || ''}`.trim());

        setClCodeAgence(cl.code_ag || cl.Code_agence || '');
        setClCodeGuichet(cl.code_gu || cl.Code_guichet || '');
        setClCompte(cl.compte || cl.Compte || '');
        setClCleRib(cl.cle_rib || cl.Cle_rib || '');
        setClVille(cl.Ville || '');
    };

    // ─── Client — jsPDF ───────────────────────────────────────────────────────────────

    const createTraiteClient = () => {
        const m = [];
        if (!crea) m.push('Date Création');
        if (!eche) m.push('Date Échéance');
        if (!numEffet) m.push('N° Effet');
        if (!valeur) m.push('Montant TTC');
        if (!clNom) m.push('Nom client');
        if (!clCodeAgence) m.push('Code Agence');
        if (!clCodeGuichet) m.push('Code Guichet');
        if (!clCompte) m.push('Compte');
        if (!clCleRib) m.push('Clé RIB');
        if (!clVille) m.push('Domiciliation');
        if (m.length > 0) { alert('Champs manquants : ' + m.join(', ')); return; }
        generatePdf(
            atcRib,
            { codeAgence: clCodeAgence, codeGuichet: clCodeGuichet, compte: clCompte, cleRib: clCleRib, ville: clVille },
            { nom: clNom, adresse: clAdresse, cp: clCp },
            `${clNom}_${eche}_${valeur}\u20AC.pdf`
        );
    };

    // ─── Date utilities ────────────────────────────────────────────────────────

    const lastDayOf = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const formatDateDMY = (date) => {
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${d}-${m}-${date.getFullYear()}`;
    };

    const formatSqlToDMY = (sqlDate) => {
        const [y, m, d] = sqlDate.split('-');
        return `${d}-${m}-${y}`;
    };

    // ─── PDF drawing ─────────────────────────────────────────────────────────

    const drawTraitePage = (doc, montantUnique, ideffect, dateEcheance, signatureDataUrl = null, ribTireur = '', ribTire = {}, nomTire = {}) => {
        const tireCA = ribTire.codeAgence || '';
        const tireCG = ribTire.codeGuichet || '';
        const tireC = ribTire.compte || '';
        const tireCle = ribTire.cleRib || '';
        const tireVil = ribTire.ville || '';
        const tireNom = nomTire.nom || '';
        const tireAdr = nomTire.adresse || '';
        const tireCp = nomTire.cp || '';
        const H = 180; // traite top Y in mm
        const amt = `${montantUnique} \u20AC`;

        doc.setLineWidth(0.3);

        // ── Outer box ──────────────────────────────────────────────────────
        doc.rect(10, H, 190, 95);

        // ── N° effect ──────────────────────────────────────────────────────
        doc.setFontSize(10);
        cellText(doc, `N\u00B0 ${ideffect}`, 14, H + 3, 12, 5, 'L');

        // ── Right header ───────────────────────────────────────────────────
        doc.text('Contre cette LETTRE DE CHANGE', 135, tY(doc, H + 3, 5));
        doc.text('stipul\u00E9e SANS FRAIS', 135, tY(doc, H + 8, 5));
        doc.text('veuillez payer \u00E0 l ordre de nous meme', 135, tY(doc, H + 13, 5));

        // ── Header row (labels + value boxes) ──────────────────────────────
        doc.setFontSize(7);

        // MONTANT POUR CONTROLE
        drawLabeledLine(doc, 'MONTANT POUR CONTROLE', H + 25, 14, 50);
        drawBorder(doc, 14, H + 25, 36, 6, 'lbr');
        cellText(doc, amt, 14, H + 25, 36, 6, 'C');

        // DATE DE CREATION
        drawLabeledLine(doc, 'DATE DE CREATION', H + 25, 53, 78);
        drawBorder(doc, 53, H + 25, 25, 6, 'lbr');
        cellText(doc, formatSqlToDMY(crea), 53, H + 25, 25, 6, 'C');

        // ECHEANCE
        drawLabeledLine(doc, 'ECHEANCE', H + 25, 81, 106);
        drawBorder(doc, 81, H + 25, 25, 6, 'lbr');
        cellText(doc, dateEcheance, 81, H + 25, 25, 6, 'C');

        // R.I.B (supplier account)
        drawLabeledLine(doc, 'R.I.B', H + 25, 109, 159);
        drawBorder(doc, 109, H + 25, 50, 6, 'lbr');
        cellText(doc, ribTireur, 109, H + 25, 50, 6, 'C');

        // MONTANT (far right)
        drawLabeledLine(doc, 'MONTANT', H + 25, 162, 192);
        drawBorder(doc, 162, H + 25, 30, 6, 'lbr');
        cellText(doc, amt, 162, H + 25, 30, 6, 'C');

        // ── R.I.B du TIRE (H+45) ───────────────────────────────────────────
        doc.line(14, H + 45, 14, H + 55); // left
        doc.line(94, H + 45, 94, H + 55); // right
        doc.line(14, H + 45, 39, H + 45); // partial top left
        doc.line(69, H + 45, 94, H + 45); // partial top right
        cellText(doc, 'R.I.B du TIRE', 39, H + 42.5, 30, 5, 'C');

        // Code établ.
        doc.line(32, H + 47, 32, H + 55);
        cellText(doc, tireCA, 14, H + 47, 18, 4, 'C');
        cellText(doc, 'Code \u00E9tabl.', 14, H + 51, 18, 4, 'C');

        // Code guichet
        doc.line(50, H + 47, 50, H + 55);
        cellText(doc, tireCG, 32, H + 47, 18, 4, 'C');
        cellText(doc, 'Code guichet', 32, H + 51, 18, 4, 'C');

        // N°de compte
        doc.line(85, H + 47, 85, H + 55);
        cellText(doc, tireC, 50, H + 47, 35, 4, 'C');
        cellText(doc, 'N\u00B0de compte', 50, H + 51, 35, 4, 'C');

        // Clé RIB
        cellText(doc, tireCle, 85, H + 47, 9, 4, 'C');
        cellText(doc, 'Cl\u00E9 RIB', 85, H + 51, 9, 4, 'C');

        // ── DOMICILIATION (H+45, right side) ───────────────────────────────
        drawBorder(doc, 110, H + 45, 80, 15, 'lbr');
        doc.line(110, H + 45, 135, H + 45); // partial top left
        doc.line(165, H + 45, 190, H + 45); // partial top right
        cellText(doc, 'DOMICILIATION', 135, H + 42.5, 30, 5, 'C');
        cellText(doc, tireVil, 110, H + 50, 80, 5, 'C');

        // ── Valeur en / Marchandises (H+60) ────────────────────────────────
        doc.setFontSize(7);
        doc.line(14, H + 70, 29, H + 70);
        cellText(doc, 'Valeur en :', 14, H + 60, 15, 10, 'C');
        doc.setFontSize(11);
        doc.line(29, H + 70, 59, H + 70);
        cellText(doc, 'Marchandises', 29, H + 60, 30, 10, 'C');

        // ── ACCEPTATION OU AVAL (H+70) ─────────────────────────────────────
        doc.setFontSize(9);
        cellText(doc, 'ACCEPTATION OU AVAL', 14, H + 70, 80, 10, 'L');

        // ── NOM / ADRESSE / du TIRE (H+65) ────────────────────────────────
        doc.setFontSize(7);
        doc.line(107, H + 65, 107, H + 75); // vertical separator

        cellText(doc, 'NOM', 90, H + 65, 15, 3, 'R');
        cellText(doc, tireNom, 110, H + 65, 33, 3, 'L');

        cellText(doc, 'et ADRESSE', 90, H + 68, 15, 3, 'R');
        cellText(doc, tireAdr, 110, H + 68, 33, 3, 'L');

        cellText(doc, 'du TIRE', 90, H + 71, 15, 3, 'R');
        cellText(doc, tireCp, 110, H + 71, 33, 3, 'L');

        cellText(doc, 'signature', 165, H + 62, 15, 3, 'R');

        // ── Signature image (X=145, Y=H+70, width=25) ─────────────────────
        if (signatureDataUrl) {
            doc.addImage(signatureDataUrl, 'JPEG', 145, H + 70, 25, 0);
        }

        // ── Bottom open box (H+82) ─────────────────────────────────────────
        drawBorder(doc, 10, H + 82, 190, 10, 'LR');
    };

    // ─── PDF génération partagée ────────────────────────────────────────────────

    const createTraiteFournisseur = () => {
        const m = [];
        if (!crea) m.push('Date Création');
        if (!eche) m.push('Date Échéance');
        if (!numEffet) m.push('N° Effet');
        if (!valeur) m.push('Montant TTC');
        if (!codeAgence) m.push('Code Agence');
        if (!codeGuichet) m.push('Code Guichet');
        if (!compte) m.push('Compte');
        if (!cleRib) m.push('Clé RIB');
        if (!globalVille) m.push('Domiciliation');
        if (!leCompteSupplier) m.push('Compte Fournisseur');
        if (!raisonSocialeSupplier) m.push('Raison Sociale Fournisseur');
        if (m.length > 0) { alert('Champs manquants : ' + m.join(', ')); return; }
        generatePdf(
            leCompteSupplier,
            { codeAgence, codeGuichet, compte, cleRib, ville: globalVille },
            { nom: 'SARL ATC', adresse: '27 Rue Vacon', cp: '13001 MARSEILLE' },
            `${raisonSocialeSupplier}_${eche}_${valeur}€.pdf`
        );
    };

    const generatePdf = async (ribTireur, ribTire, nomTire, fileName) => {
        setLoading(true);
        try {
            const logoDataUrl = await new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const nw = img.naturalWidth > 0 ? img.naturalWidth : 140;
                    const nh = img.naturalHeight > 0 ? img.naturalHeight : 66;
                    const scale = 8;
                    const canvas = document.createElement('canvas');
                    canvas.width = nw * scale; canvas.height = nh * scale;
                    const ctx = canvas.getContext('2d');
                    ctx.scale(scale, scale); ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = () => resolve(null);
                img.src = logoSrc;
            });
            const signatureDataUrl = signatureSrc || null;
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const totalHT = parseFloat(valeur);
            const decoupe = Math.max(1, parseInt(nbDecoupe) || 1);
            let montanTraite, montanTraiteInitiale;
            if (totalHT % decoupe === 0) {
                montanTraite = montanTraiteInitiale = totalHT / decoupe;
            } else {
                montanTraite = Math.floor(totalHT / decoupe);
                montanTraiteInitiale = totalHT - (decoupe - 1) * montanTraite;
            }
            let d = new Date(eche + 'T12:00:00');
            for (let i = 0; i < decoupe; i++) {
                const montantTTC = i === 0 ? montanTraiteInitiale : montanTraite;
                if (i !== 0) { d = new Date(d.getTime() + 86400000); d = lastDayOf(d); }
                const dateEffetok = formatDateDMY(d);
                if (i > 0) doc.addPage();
                if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', 90, 15, 30, 14);
                else { doc.setFontSize(14); doc.text('ATC', 105, 23, { align: 'center' }); }
                drawTraitePage(doc, montantTTC, parseInt(numEffet) + i, dateEffetok, signatureDataUrl,
                    ribTireur, ribTire, nomTire);
                if (i === 0) d = lastDayOf(d);
            }
            doc.save(fileName);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => mode === 0 ? createTraiteClient() : createTraiteFournisseur();

    return (
        <div style={s.page}>
            <p style={s.pageTitle}>Traite</p>

            <Paper style={s.tabsWrap} elevation={0}>
                <Tabs value={mode} onChange={(_, v) => setMode(v)} indicatorColor="primary" textColor="primary">
                    <Tab label="Client" style={{ minWidth: 140, fontSize: 14, fontWeight: 500, textTransform: 'none' }} />
                    <Tab label="Fournisseur" style={{ minWidth: 140, fontSize: 14, fontWeight: 500, textTransform: 'none' }} />
                </Tabs>
            </Paper>

            <div style={s.layout}>
                <div style={s.leftCol}>
                    {mode === 0 ? (
                        <>
                            <Paper style={s.card} elevation={0}>
                                <p style={s.cardTitle}>Tiré — Client</p>
                                <Autocomplete
                                    options={clientList}
                                    getOptionLabel={(o) => o.label || o.Nom_agence || ''}
                                    getOptionSelected={(o, v) => (o.id || o.id_bij || o.Code_Agence) === (v.id || v.id_bij || v.Code_Agence)}
                                    onChange={(_, v) => handleClientSelect(v)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Client" variant="outlined" margin="dense" fullWidth />
                                    )}
                                />
                                {clNom && (
                                    <div style={{ marginTop: 12, padding: '10px 14px', background: '#e8f0fe', borderRadius: 6, fontSize: 12, color: '#3c4043', lineHeight: 1.7 }}>
                                        <strong style={{ color: '#1a73e8', fontSize: 13 }}>{clNom}</strong>
                                        {clAdresse && <><br />{clAdresse}</>}
                                        {clCp && <><br />{clCp}</>}
                                    </div>
                                )}
                                <Divider style={{ margin: '16px 0 8px' }} />
                                <p style={{ ...s.cardTitle, margin: '0 0 8px' }}>Coordonnées bancaires</p>
                                <div style={{ ...s.row, marginTop: 4 }}>
                                    <TextField style={s.col} label="Code agence" value={clCodeAgence} onChange={(e) => setClCodeAgence(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={s.col} label="Code guichet" value={clCodeGuichet} onChange={(e) => setClCodeGuichet(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={s.col} label="Compte" value={clCompte} onChange={(e) => setClCompte(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={{ flex: '0 0 80px' }} label="Clé RIB" value={clCleRib} onChange={(e) => setClCleRib(e.target.value)} variant="outlined" margin="dense" />
                                </div>
                                <TextField fullWidth label="Domiciliation" value={clVille} onChange={(e) => setClVille(e.target.value)} variant="outlined" margin="dense" style={{ marginTop: 4 }} />
                            </Paper>

                            <Paper style={s.card} elevation={0}>
                                <p style={s.cardTitle}>Tireur — ATC</p>
                                <Autocomplete
                                    options={banks}
                                    getOptionLabel={(o) => o.label}
                                    getOptionSelected={(o, v) => o.value === v.value}
                                    onChange={(_, v) => handleAtcBankChange(v?.value || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Banque ATC" variant="outlined" margin="dense" fullWidth />
                                    )}
                                />
                                <TextField fullWidth label="RIB ATC" value={atcRib} onChange={(e) => setAtcRib(e.target.value)} variant="outlined" margin="dense" style={{ marginTop: 4 }} />
                            </Paper>
                        </>
                    ) : (
                        <>
                            <Paper style={s.card} elevation={0}>
                                <p style={s.cardTitle}>Banque ATC (tiré)</p>
                                <Autocomplete
                                    options={banks}
                                    getOptionLabel={(o) => o.label}
                                    getOptionSelected={(o, v) => o.value === v.value}
                                    onChange={(_, v) => handleBankChange(v?.value || '')}
                                    renderInput={(params) => <TextField {...params} label="Banque" variant="outlined" margin="dense" fullWidth />}
                                />
                                <div style={{ ...s.row, marginTop: 8 }}>
                                    <TextField style={s.col} label="Code agence" value={codeAgence} onChange={(e) => setCodeAgence(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={s.col} label="Code guichet" value={codeGuichet} onChange={(e) => setCodeGuichet(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={s.col} label="Compte" value={compte} onChange={(e) => setCompte(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={{ flex: '0 0 80px' }} label="Clé RIB" value={cleRib} onChange={(e) => setCleRib(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={{ flex: '1 1 200px' }} label="Domiciliation" value={globalVille} onChange={(e) => setGlobalVille(e.target.value)} variant="outlined" margin="dense" />
                                </div>
                            </Paper>
                            <Paper style={s.card} elevation={0}>
                                <p style={s.cardTitle}>Fournisseur (tireur)</p>
                                <Autocomplete
                                    options={suppliers}
                                    getOptionLabel={(o) => o.label}
                                    getOptionSelected={(o, v) => o.value === v.value}
                                    onChange={(_, v) => handleBankSupplier(v?.value || '')}
                                    defaultValue={suppliers[0]}
                                    renderInput={(params) => <TextField {...params} label="Fournisseur" variant="outlined" margin="dense" fullWidth />}
                                />
                                <div style={{ ...s.row, marginTop: 8 }}>
                                    <TextField style={s.col} label="Raison sociale" value={raisonSocialeSupplier} onChange={(e) => setRaisonSocialeSupplier(e.target.value)} variant="outlined" margin="dense" />
                                    <TextField style={s.col} label="RIB / Compte" value={leCompteSupplier} onChange={(e) => setLeCompteSupplier(e.target.value)} variant="outlined" margin="dense" />
                                </div>
                            </Paper>
                        </>
                    )}
                </div>

                <div style={s.rightCol}>
                    <Paper style={s.card} elevation={0}>
                        <p style={s.cardTitle}>Paramètres</p>
                        <TextField fullWidth type="date" value={crea} onChange={(e) => setCrea(e.target.value)} label="Date création" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth type="date" value={eche} onChange={(e) => setEche(e.target.value)} label="Date échéance" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth value={valeur} onChange={(e) => setValeur(e.target.value)} label="Montant TTC" variant="outlined" margin="dense" />
                        <TextField fullWidth value={numEffet} onChange={(e) => setNumEffet(e.target.value)} label="N° Effet" variant="outlined" margin="dense" />
                        <TextField fullWidth type="number" value={nbDecoupe} onChange={(e) => setNbDecoupe(e.target.value)} label="Nb découpe" variant="outlined" margin="dense" inputProps={{ min: 1 }} />
                        <Divider style={s.divider} />
                        <button
                            style={{ ...s.btn, ...(loading ? { opacity: 0.65, cursor: 'not-allowed' } : {}) }}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading && <CircularProgress size={16} style={{ color: '#fff' }} />}
                            {mode === 0 ? 'Générer traite client' : 'Générer traite fournisseur'}
                        </button>
                    </Paper>
                </div>
            </div>
        </div>
    );
};

export default Traite;